import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createBlock as _createBlock, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "tab-wrap" }
const _hoisted_2 = { class: "tab-bar" }

import { ref, watch, computed, toRef } from 'vue'
import type { Conversation, PopupData } from '@/types/dashboard'
import { useSLANotifications } from '@/composables/useSLANotifications'
import { usePermission } from '@/composables/usePermission'
import AgentsTab              from './AgentsTab.vue'
import PrioridadesTab         from './PrioridadesTab.vue'
import ForaDeHorarioTab       from './ForaDeHorarioTab.vue'
import AguardandoRespostaTab  from './AguardandoRespostaTab.vue'
import SLATab                 from './SLATab.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'TabsPanel',
  props: {
    conversations: {},
    allAgents: {},
    agentTeamsMap: {},
    toleranceMinutes: {},
    scopeReady: { type: Boolean }
  },
  emits: ["showPopup", "hidePopup"],
  setup(__props: any, { emit: __emit }) {

const props = __props

useSLANotifications(toRef(props, 'conversations'))

// Conversas com a etiqueta "avaliacao_atendimento" (fluxo interno de avaliação/CSAT do bot, ver
// AvaliacoesView) nunca devem aparecer nas abas de trabalho do dashboard — mesmo critério já usado
// em SLATab.vue e useMyConversations.ts. Filtra aqui, uma vez só, e repassa pra todas as abas
// abaixo, em vez de cada uma filtrar por conta própria (evita divergência entre abas).
const visibleConversations = computed(() =>
  props.conversations.filter(c => !c.labels.includes('avaliacao_atendimento'))
)

const emit = __emit

const { can } = usePermission()

const canViewAgents = computed(() => can('dashboard.viewAgents'))

// First allowed default tab
const defaultTab = computed(() => canViewAgents.value ? 'agentes' : 'prioridades')

const activeTab       = ref(defaultTab.value)
const prioBadge       = ref(0)
const foraBadge       = ref(0)
const aguardandoBadge = ref(0)
const slaBadge        = ref(0)

// If the active tab gets revoked, jump to the first allowed one
watch(canViewAgents, () => {
  if (activeTab.value === 'agentes' && !canViewAgents.value) activeTab.value = defaultTab.value
})

// Auto-foca na aba "Fora de Horário" quando os dados carregam e há conversas com essa etiqueta
watch(
  () => visibleConversations.value.length,
  (newLen, oldLen) => {
    if (oldLen === 0 && newLen > 0) {
      const hasForaLabel = visibleConversations.value.some(c =>
        c.labels.some(lbl => lbl.toLowerCase().replace(/[\s_]+/g, '-') === 'atendimento-fora-de-horario')
      )
      if (hasForaLabel) activeTab.value = 'fora-de-horario'
    }
  }
)

function onShowPopup(data: PopupData, el: HTMLElement) {
  emit('showPopup', data, el)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      (canViewAgents.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            class: _normalizeClass(["tab-btn", { active: activeTab.value === 'agentes' }]),
            onClick: _cache[0] || (_cache[0] = ($event: any) => (activeTab.value = 'agentes'))
          }, [...(_cache[11] || (_cache[11] = [
            _createElementVNode("svg", {
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              "stroke-width": "2",
              "stroke-linecap": "round",
              "stroke-linejoin": "round"
            }, [
              _createElementVNode("circle", {
                cx: "12",
                cy: "8",
                r: "4"
              }),
              _createElementVNode("path", { d: "M20 21a8 8 0 1 0-16 0" })
            ], -1),
            _createTextVNode(" Agentes ", -1)
          ]))], 2))
        : _createCommentVNode("", true),
      _createElementVNode("button", {
        class: _normalizeClass(["tab-btn", { active: activeTab.value === 'prioridades' }]),
        onClick: _cache[1] || (_cache[1] = ($event: any) => (activeTab.value = 'prioridades'))
      }, [
        _cache[12] || (_cache[12] = _createElementVNode("svg", {
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          "stroke-width": "2",
          "stroke-linecap": "round",
          "stroke-linejoin": "round"
        }, [
          _createElementVNode("circle", {
            cx: "12",
            cy: "12",
            r: "10"
          }),
          _createElementVNode("line", {
            x1: "12",
            y1: "8",
            x2: "12",
            y2: "12"
          }),
          _createElementVNode("line", {
            x1: "12",
            y1: "16",
            x2: "12.01",
            y2: "16"
          })
        ], -1)),
        _cache[13] || (_cache[13] = _createTextVNode(" Prioridades ", -1)),
        _createElementVNode("span", {
          class: _normalizeClass(["tab-notif tab-notif-prio", { zero: prioBadge.value === 0 }])
        }, _toDisplayString(prioBadge.value), 3)
      ], 2),
      _createElementVNode("button", {
        class: _normalizeClass(["tab-btn", { active: activeTab.value === 'fora-de-horario' }]),
        onClick: _cache[2] || (_cache[2] = ($event: any) => (activeTab.value = 'fora-de-horario'))
      }, [
        _cache[14] || (_cache[14] = _createElementVNode("svg", {
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          "stroke-width": "2",
          "stroke-linecap": "round",
          "stroke-linejoin": "round"
        }, [
          _createElementVNode("path", { d: "M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" })
        ], -1)),
        _cache[15] || (_cache[15] = _createTextVNode(" Fora de Horário ", -1)),
        _createElementVNode("span", {
          class: _normalizeClass(["tab-notif tab-notif-fora", { zero: foraBadge.value === 0 }])
        }, _toDisplayString(foraBadge.value), 3)
      ], 2),
      _createElementVNode("button", {
        class: _normalizeClass(["tab-btn", { active: activeTab.value === 'aguardando-resposta' }]),
        onClick: _cache[3] || (_cache[3] = ($event: any) => (activeTab.value = 'aguardando-resposta'))
      }, [
        _cache[16] || (_cache[16] = _createElementVNode("svg", {
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          "stroke-width": "2",
          "stroke-linecap": "round",
          "stroke-linejoin": "round"
        }, [
          _createElementVNode("circle", {
            cx: "12",
            cy: "12",
            r: "10"
          }),
          _createElementVNode("polyline", { points: "12 6 12 12 16 14" })
        ], -1)),
        _cache[17] || (_cache[17] = _createTextVNode(" Aguardando ", -1)),
        _createElementVNode("span", {
          class: _normalizeClass(["tab-notif tab-notif-agrd", { zero: aguardandoBadge.value === 0 }])
        }, _toDisplayString(aguardandoBadge.value), 3)
      ], 2),
      _createElementVNode("button", {
        class: _normalizeClass(["tab-btn", { active: activeTab.value === 'sla' }]),
        onClick: _cache[4] || (_cache[4] = ($event: any) => (activeTab.value = 'sla'))
      }, [
        _cache[18] || (_cache[18] = _createElementVNode("svg", {
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          "stroke-width": "2",
          "stroke-linecap": "round",
          "stroke-linejoin": "round"
        }, [
          _createElementVNode("path", { d: "M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" }),
          _createElementVNode("line", {
            x1: "12",
            y1: "9",
            x2: "12",
            y2: "13"
          }),
          _createElementVNode("line", {
            x1: "12",
            y1: "17",
            x2: "12.01",
            y2: "17"
          })
        ], -1)),
        _cache[19] || (_cache[19] = _createTextVNode(" SLA ", -1)),
        _createElementVNode("span", {
          class: _normalizeClass(["tab-notif tab-notif-sla", { zero: slaBadge.value === 0 }])
        }, _toDisplayString(slaBadge.value), 3)
      ], 2)
    ]),
    (canViewAgents.value)
      ? (_openBlock(), _createBlock(AgentsTab, {
          key: 0,
          conversations: visibleConversations.value,
          "all-agents": __props.allAgents ?? [],
          "agent-teams-map": __props.agentTeamsMap,
          "scope-ready": __props.scopeReady,
          active: activeTab.value === 'agentes',
          onShowPopup: onShowPopup,
          onHidePopup: _cache[5] || (_cache[5] = ($event: any) => (emit('hidePopup')))
        }, null, 8, ["conversations", "all-agents", "agent-teams-map", "scope-ready", "active"]))
      : _createCommentVNode("", true),
    _createVNode(PrioridadesTab, {
      conversations: visibleConversations.value,
      active: activeTab.value === 'prioridades',
      onUpdateBadge: _cache[6] || (_cache[6] = ($event: any) => (prioBadge.value = $event))
    }, null, 8, ["conversations", "active"]),
    _createVNode(ForaDeHorarioTab, {
      conversations: visibleConversations.value,
      active: activeTab.value === 'fora-de-horario',
      onUpdateBadge: _cache[7] || (_cache[7] = ($event: any) => (foraBadge.value = $event))
    }, null, 8, ["conversations", "active"]),
    _createVNode(AguardandoRespostaTab, {
      conversations: visibleConversations.value,
      active: activeTab.value === 'aguardando-resposta',
      "tolerance-minutes": __props.toleranceMinutes,
      onUpdateBadge: _cache[8] || (_cache[8] = ($event: any) => (aguardandoBadge.value = $event))
    }, null, 8, ["conversations", "active", "tolerance-minutes"]),
    _createVNode(SLATab, {
      conversations: visibleConversations.value,
      active: activeTab.value === 'sla',
      onUpdateBadge: _cache[9] || (_cache[9] = ($event: any) => (slaBadge.value = $event)),
      onShowPopup: onShowPopup,
      onHidePopup: _cache[10] || (_cache[10] = ($event: any) => (emit('hidePopup')))
    }, null, 8, ["conversations", "active"])
  ]))
}
}

})